import {
  Input,
  Injectable,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostBinding,
  Inject
} from '@angular/core';
import {
  FormControlState,
  unbox,
  KeyValue
} from 'ngrx-forms';
import { WrappedFormControl } from './wrapped-form-control';
import { NgrxErrorStateMatcher } from './ngrx-error-state-matcher';
import { Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  debounceTime,
  skipWhile,
  map
} from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import {
  FORM_CONTROL_REQUIRED_KEY,
  FORM_CONTROL_PLACEHOLDER_KEY,
  FORM_CONTROL_FX_FLEX_KEY,
  FORM_CONTROL_FX_HIDE_KEY,
  FORM_CONTROL_LABEL_KEY
} from './form-control';
import {
  ON_EVENT_USER_DEFINED_PROPERTY,
  SET_INPUT_USER_DEFINED_PROPERTY
} from './decorators/controls/user-defined-property-keys';
import { OnEventMapEntry } from './decorators/controls/on-event';
import { SetInputMapEntry } from './decorators/controls/set-input';
import { FORM_FIELD_APPEARANCE } from './tokens';

@Injectable()
export abstract class AbstractInputComponent<
  V,
  F extends FormControlState<any> = FormControlState<any>
> implements OnInit, OnDestroy {
  public get ngrxFormControl(): F {
    return this.formControl.ngrxFormControl;
  }

  @Input() public appearance: string;

  @Input('ngrxFormControlState')
  public set ngrxFormControl(value: F) {
    this.formControl.ngrxFormControl = value;
    if (this.ngrxFormControl) {
      this.processUserDefinedProperties(this.ngrxFormControl.userDefinedProperties);
    }
  }

  public get controlId(): string {
    return this.formControl && this.formControl.ngrxFormControl && this.formControl.ngrxFormControl.id;
  }

  public get disabled(): boolean {
    return this.formControl.disabled;
  }

  @Input()
  public set userDefinedProperties(value: KeyValue) {
    this.processUserDefinedProperties(value);
  }

  public get userDefinedProperties(): KeyValue {
    return this.ngrxFormControl ? this.ngrxFormControl.userDefinedProperties : {};
  }

  @Input()
  public set disabled(value: boolean) {
    if (value) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  public get value(): any | V {
    return this.formControl.ngrxValue;
  }

  @Input()
  public set value(value: any | V) {
    if (!this._initialized || this.formControl.hasChanged(value)) {
      this.setValue(value, !this._initialized);
    }
  }

  public get boxed(): boolean | '' {
    return this.formControl.boxed;
  }

  @Input()
  public set boxed(value: boolean | '') {
    this.formControl.boxed = value || value === '';
  }

  @Input()
  public set isEqualFnc(equal: (a: any, b: any) => boolean) {
    this.formControl.isEqual = equal.bind(this.formControl);
  }

  @Input()
  public set standalone(value: boolean | '') {
    this.formControl.standalone = value || value === '';
  }

  @Input()
  public set toDisplayFnc(toDisplay: (value: V) => string) {
    this.formControl.toDisplayValue = toDisplay;
  }

  private _required = false;

  public get required(): boolean | '' {
    if (this.ngrxFormControl) {
      return this.ngrxFormControl.userDefinedProperties.hasOwnProperty(
        FORM_CONTROL_REQUIRED_KEY
      )
             ? this.ngrxFormControl.userDefinedProperties[ FORM_CONTROL_REQUIRED_KEY ]
             : this._required;
    } else {
      return this._required;
    }
  }

  public get fxFlex(): string {
    return this.ngrxFormControl && this.ngrxFormControl.userDefinedProperties[FORM_CONTROL_FX_FLEX_KEY];
  }

  public get fxHide(): boolean {
    return this.ngrxFormControl && this.ngrxFormControl.userDefinedProperties[FORM_CONTROL_FX_HIDE_KEY];
  }

  @Input()
  public set required(value: boolean | '') {
    this._required = value || value === '';
  }

  public get readonly(): boolean | '' {
    return this.formControl.readonly;
  }

  @Input()
  public set readonly(value: boolean | '') {
    this.formControl.readonly = value || value === '';
    // TODO : move to wrapped form control
    this.formControl.readonlyChanges$.next(this.formControl.readonly);
  }

  public get invalid(): boolean {
    return !this._valid;
  }

  @Input()
  public set invalid(value: boolean) {
    this.formControl.setErrors(value ? {} : null);
    this._valid = !value;
  }

  private _valid = true;

  public get valid(): boolean {
    return this._valid;
  }

  @Input()
  public set valid(value: boolean) {
    this._valid = value;
  }

  protected _initialized = false;

  public get initialized(): boolean {
    return this._initialized;
  }

  @Input() public width = '100%';
  @Output()
  /**
   * @deprecated use valueChanged
   */
  public change = new EventEmitter<V>();
  @Output()
  public valueChanged = new EventEmitter<V>();
  @Input() public placeholder = '';
  @Input() public label = '';

  @HostBinding('style.display') public styleDisplay: string = undefined;

  // add _ prefix to allow tabIndex pass throw
  @Input() public hint = '';
  // tslint:disable-next-line:no-input-rename
  @Input('control') public formControl = new WrappedFormControl<V, F>();
  // tslint:disable-next-line:no-input-rename
  @Input('_tabIndex') public tabIndex: number | undefined = undefined;
  // tslint:disable-next-line:no-input-rename
  @Input('debounceTime') public debounceTime = 500;
  protected _subscription = new Subscription();
  private _onEventSubscriptions: string[] = [];

  public constructor(
    public matcher: NgrxErrorStateMatcher,
    @Inject(FORM_FIELD_APPEARANCE) appearance?: string,
  ) {
    this.appearance = appearance || 'legacy';
    this.isEqual = this.isEqual.bind(this);
    this.toDisplayValue = this.toDisplayValue.bind(this);
    this.addToArray = this.addToArray.bind(this);
    this.removeFromArray = this.removeFromArray.bind(this);
    this.hasChanged = this.hasChanged.bind(this);
    this.copyStatus = this.copyStatus.bind(this);
    this.trackBy = this.trackBy.bind(this);

    this._subscription.add(
      this.formControl.ngrxValueChanges$
        .pipe(
          skipWhile(() => this._initialized === false),
          debounceTime(this.debounceTime),
          distinctUntilChanged((a, b) => !this.hasChanged(a, b))
        )
        .subscribe((value: V) => {
          // TODO : fix types
          this.change.emit(unbox(value) as any);
          this.valueChanged.emit(unbox(value) as any);
        })
    );
    this._subscription.add(
      this.formControl.statusChanges
        .pipe(
          distinctUntilChanged(),
          map(status => status !== 'INVALID')
        )
        .subscribe(valid => (this.valid = valid))
    );
  }

  public processUserDefinedProperties(userDefinedProperties: KeyValue): void {
    if (
      userDefinedProperties.hasOwnProperty(
        FORM_CONTROL_PLACEHOLDER_KEY
      )
    ) {
      if (
        userDefinedProperties[
          FORM_CONTROL_PLACEHOLDER_KEY
          ] !== this.placeholder
      ) {
        this.placeholder = userDefinedProperties[
          FORM_CONTROL_PLACEHOLDER_KEY
          ];
      }
    }
    if (
      userDefinedProperties.hasOwnProperty(
        FORM_CONTROL_LABEL_KEY
      )
    ) {
      if (
        userDefinedProperties[
          FORM_CONTROL_LABEL_KEY
          ] !== this.label
      ) {
        this.label = userDefinedProperties[
          FORM_CONTROL_LABEL_KEY
          ];
      }
    }
    if (
      userDefinedProperties.hasOwnProperty(
        FORM_CONTROL_FX_HIDE_KEY
      )
    ) {
      if (
        userDefinedProperties[ FORM_CONTROL_FX_HIDE_KEY ]
      ) {
        this.styleDisplay = 'none';
      } else {
        this.styleDisplay = undefined;
      }
    }
    if (userDefinedProperties.hasOwnProperty(ON_EVENT_USER_DEFINED_PROPERTY)) {
      for (const onEvent of userDefinedProperties[ ON_EVENT_USER_DEFINED_PROPERTY ] as Array<OnEventMapEntry>) {

        if (this.hasOwnProperty(onEvent.output) && this[ onEvent.output ] instanceof EventEmitter) {

          if (!this._onEventSubscriptions.find(output => output === onEvent.output)) {
            this._subscription.add((<EventEmitter<any>>this[ onEvent.output ]).subscribe($event => onEvent.handler($event)));
            this._onEventSubscriptions.push(onEvent.output);
          }

        } else {
          console.warn(`Defined output '${onEvent.output}' is not found on control '${Object.getPrototypeOf(this).constructor.name}'`);
        }

      }
    }
    if (userDefinedProperties.hasOwnProperty(SET_INPUT_USER_DEFINED_PROPERTY)) {
      for (const setInput of userDefinedProperties[ SET_INPUT_USER_DEFINED_PROPERTY ] as Array<SetInputMapEntry>) {

        if (this.hasOwnProperty(setInput.input)) {

          this[setInput.input] = setInput.value;

        } else {
          console.warn(`Defined input '${setInput.input}' is not found on control '${Object.getPrototypeOf(this).constructor.name}'`);
        }

      }
    }
  }

  public setValue(value: V | null, emitEvent: boolean = true): void {
    this.formControl.setValue(value, { emitEvent });
  }

  public isEqual(a: any, b: any): boolean {
    return this.formControl.isEqual(a, b);
  }

  public toDisplayValue(value: V): string {
    return this.formControl.toDisplayValue(value);
  }

  public addToArray(item: any, end = true): void {
    this.formControl.addToArray(item, end);
  }

  public removeFromArray(item: any): void {
    this.formControl.removeFromArray(item);
  }

  public hasChanged(a: V, b?: V): boolean {
    return this.formControl.hasChanged(a, b);
  }

  public ngOnInit(): void {
    this._initialized = true;
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._subscription = new Subscription();
    this._onEventSubscriptions = [];
    this.formControl.destroy();
  }

  public trackBy(index: number, item: any) {
    return item.value.hasOwnProperty('id') ? item.value.id : index;
  }

  public copyStatus(formControl: FormControl): void {
    this.formControl.copyStatus(formControl);
  }
}
